<template>
  <div class="rts-wrapper">
    <!-- ============ ORDER TYPE PICKER ============ -->
    <div v-if="!order_type" class="rts-picker">
      <h1 class="rts-picker-title">Ready to Schedule</h1>
      <p class="rts-picker-sub">Choose what you want to schedule this week</p>
      <div class="rts-picker-cards">
        <div class="rts-pick-card" @click="pickOrderType('home_delivery')">
          <i class="fas fa-truck"></i>
          <div class="rts-pick-label">Home Delivery</div>
          <div class="rts-pick-count">
            <span v-if="pickerCounts.loading">…</span>
            <span v-else>{{ pickerCounts.home_delivery }}</span>
          </div>
          <div class="rts-pick-sub">orders ready</div>
        </div>
        <div class="rts-pick-card" @click="pickOrderType('store_pickup')">
          <i class="fas fa-store"></i>
          <div class="rts-pick-label">Store Pickup</div>
          <div class="rts-pick-count">
            <span v-if="pickerCounts.loading">…</span>
            <span v-else>{{ pickerCounts.store_pickup }}</span>
          </div>
          <div class="rts-pick-sub">orders ready</div>
        </div>
      </div>
    </div>

    <!-- ============ LEVEL 1: WEEK OVERVIEW ============ -->
    <div v-else-if="!isValidDay(selectedDay)" class="rts-week-overview">
      <div class="rts-toolbar">
        <div class="rts-toolbar-left">
          <button class="rts-btn-back" @click="backToPicker">
            <i class="fas fa-arrow-left"></i> Change Type
          </button>
          <span class="rts-badge"
                :class="order_type === 'home_delivery' ? 'rts-badge-hd' : 'rts-badge-sp'">
            <i :class="order_type === 'home_delivery' ? 'fas fa-truck' : 'fas fa-store'"></i>
            {{ order_type === 'home_delivery' ? 'HOME DELIVERY' : 'STORE PICKUP' }}
          </span>
        </div>
        <div class="rts-toolbar-right">
          <button class="rts-btn-ghost" @click="syncTruckAvailability(true)"
                  :disabled="truckSync.loading">
            <i class="fas" :class="truckSync.loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
            {{ truckSync.loading ? 'Syncing…' : 'Sync Truck Availability' }}
          </button>
          <span v-if="truckSync.lastSync" class="rts-sync-meta">
            Last sync: {{ truckSync.lastSync }}
          </span>
        </div>
      </div>

      <!-- ZONE × DAY CAPACITY GRID -->
      <ZoneCapacityGrid
        :zones="zones"
        :matrix="capacityMatrix"
        :counts-by-date="countsByDate"
        :week-days="weekDays"
        :saving-cells="savingCells"
        :week-start="weekStart"
        :week-end="weekEnd"
        :truck-availability="truckAvailability"
        :daily-capacity-by-dow="dailyCapacityByDow"
        :assigned-capacity-by-dow="assignedCapacityByDow"
        @toggle-cell="onToggleCell"
        @max-change="onMaxChange"
        @open-day="openDay"
        @prev-week="prevWeek"
        @next-week="nextWeek"
        @this-week="thisWeek"
        @refresh="loadWeek"
      />
    </div>

    <!-- ============ LEVEL 2: DAY DETAIL ============ -->
    <DayDetail
      v-else
      :day="selectedDay"
      :order-type="order_type"
      :invoices="dayInvoices"
      :zone-counts="dayZoneCounts"
      :capacity="dayCapacity"
      :truck-capacity="truckAvailability[selectedDay.dateStr] || null"
      @back="closeDay"
      @prev="prevDay"
      @next="nextDay"
      @refresh="loadWeek"
      @distribute="distributeDay"
      @reschedule="openReschedModal"
    />

    <!-- RESCHEDULE MODAL -->
    <b-modal id="rts-resched-modal" title="Reschedule Delivery" hide-footer size="lg">
      <div v-if="reschedModal.invoice" class="rts-modal-body">
        <p class="rts-modal-sub">
          Invoice <b>#{{ reschedModal.invoice.invoice_id }}</b> — {{ reschedModal.invoice.fullname || '—' }}
        </p>
        <div class="rts-field">
          <label>New Delivery Date</label>
          <input type="date" v-model="reschedModal.delivery_date" class="rts-field-input" />
        </div>
        <div class="rts-modal-actions">
          <button class="rts-btn-cancel" @click="$bvModal.hide('rts-resched-modal')">Cancel</button>
          <button class="rts-btn-save" @click="saveResched">Save</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import ZoneCapacityGrid from './ZoneCapacityGrid.vue'
import DayDetail from './DayDetail.vue'

export default {
  name: 'ReadyToScheduleCalendar',
  components: { ZoneCapacityGrid, DayDetail },
  props: {
    locationId:   { type: [Number, String], default: null },
    locationName: { type: String, default: '' },
  },
  data() {
    return {
      order_type: null,
      pickerCounts: { loading: false, home_delivery: 0, store_pickup: 0 },

      weekStart: null,
      weekEnd:   null,
      selectedDay: null,

      invoicesByDate: {},
      countsByDate:   {},
      capacityMatrix: {},
      zones: [],

      // ===== Truck availability =====
      truckAvailability: {},
      weekTruckTotal: 0,
      truckSync: { loading: false, lastSync: null },

      savingCells: {},

      reschedModal: { invoice: null, delivery_date: '' },
    }
  },
  computed: {
    weekDays() {
      if (!this.weekStart) return []
      const out = []
      const today = moment().startOf('day')
      for (let i = 0; i < 7; i++) {
        const d = this.weekStart.clone().add(i, 'days')
        out.push({
          date:        d,
          dateStr:     d.format('MM-DD-YYYY'),
          dateLabel:   d.format('MMM D'),
          dateOnly:    d.format('D'),
          monthShort:  d.format('MMM'),
          dayShort:    d.format('ddd'),
          dow:         i + 1,
          isToday:     d.isSame(today, 'day'),
          isWeekend:   i >= 5,
          isPast:      d.isBefore(today, 'day'),
        })
      }
      return out
    },
    weekStartLabel() { return this.weekStart ? this.weekStart.format('MMM D, YYYY') : '' },
    weekEndLabel()   { return this.weekEnd   ? this.weekEnd.format('MMM D, YYYY')   : '' },

    totalInvoices() {
      return Object.values(this.invoicesByDate).reduce((a, b) => a + b.length, 0)
    },
    assignedCount() {
      let n = 0
      Object.values(this.countsByDate).forEach(byZone => {
        Object.values(byZone).forEach(z => { n += (z.assigned || 0) })
      })
      return n
    },

    dailyCapacityByDow() {
      const out = {}
      this.weekDays.forEach(day => {
        const rec = this.truckAvailability[day.dateStr]
        out[day.dow] = rec ? (rec.total_capacity || 0) : 0
      })
      return out
    },
    assignedCapacityByDow() {
      const out = {}
      this.weekDays.forEach(day => {
        let sum = 0
        Object.keys(this.capacityMatrix).forEach(zid => {
          const cell = (this.capacityMatrix[zid] || {})[day.dow]
          if (cell && cell.is_active) sum += (cell.max_orders || 0)
        })
        out[day.dow] = sum
      })
      return out
    },

    dayInvoices() {
      if (!this.isValidDay(this.selectedDay)) return []
      return this.invoicesByDate[this.selectedDay.dateStr] || []
    },
    dayZoneCounts() {
      if (!this.isValidDay(this.selectedDay)) return {}
      return this.countsByDate[this.selectedDay.dateStr] || {}
    },
    dayCapacity() {
      if (!this.isValidDay(this.selectedDay)) return {}
      const out = {}
      Object.keys(this.capacityMatrix).forEach(zid => {
        out[zid] = (this.capacityMatrix[zid] || {})[this.selectedDay.dow]
                || { is_active: true, max_orders: 0 }
      })
      return out
    },
  },
  created() {
    const now = moment()
    this.weekStart = now.clone().startOf('isoWeek')
    this.weekEnd   = this.weekStart.clone().add(6, 'days')
    this.loadPickerCounts()
    this.loadCapacity()
  },
  methods: {
    isValidDay(d) {
      return !!(d && typeof d === 'object' && typeof d.dateStr === 'string' && d.dateStr)
    },

    // ===== Picker =====
    loadPickerCounts() {
      this.pickerCounts.loading = true
      Requestor.getAsyncP(
        '/api/sales/get_scheduled_order_type_counts/',
        { location_id: this.locationId || undefined },
        this,
        (r) => {
          const res = r.data.result || {}
          this.pickerCounts.home_delivery = res.home_delivery || 0
          this.pickerCounts.store_pickup  = res.store_pickup  || 0
          this.pickerCounts.loading = false
        },
        () => { this.pickerCounts.loading = false }
      )
    },
    pickOrderType(type) {
      this.order_type = type
      this.selectedDay = null
      this.$nextTick(() => this.loadWeek())
    },
    backToPicker() {
      this.order_type = null
      this.selectedDay = null
      this.invoicesByDate = {}
      this.countsByDate = {}
    },

    // ===== Week navigation =====
    prevWeek() {
      this.weekStart = this.weekStart.clone().subtract(7, 'days')
      this.weekEnd   = this.weekStart.clone().add(6, 'days')
      this.selectedDay = null
      this.loadWeek()
    },
    nextWeek() {
      this.weekStart = this.weekStart.clone().add(7, 'days')
      this.weekEnd   = this.weekStart.clone().add(6, 'days')
      this.selectedDay = null
      this.loadWeek()
    },
    thisWeek() {
      this.weekStart = moment().startOf('isoWeek')
      this.weekEnd   = this.weekStart.clone().add(6, 'days')
      this.selectedDay = null
      this.loadWeek()
    },

    // ===== Day navigation =====
    openDay(day) {
      if (!this.isValidDay(day)) return
      this.selectedDay = day
    },
    closeDay() {
      this.selectedDay = null
    },
    prevDay() {
      if (!this.isValidDay(this.selectedDay)) return
      const idx = this.weekDays.findIndex(d => d.dateStr === this.selectedDay.dateStr)
      if (idx > 0) {
        this.openDay(this.weekDays[idx - 1])
      } else {
        this.weekStart = this.weekStart.clone().subtract(7, 'days')
        this.weekEnd   = this.weekStart.clone().add(6, 'days')
        this.loadWeek().then(() => this.openDay(this.weekDays[6]))
      }
    },
    nextDay() {
      if (!this.isValidDay(this.selectedDay)) return
      const idx = this.weekDays.findIndex(d => d.dateStr === this.selectedDay.dateStr)
      if (idx >= 0 && idx < 6) {
        this.openDay(this.weekDays[idx + 1])
      } else {
        this.weekStart = this.weekStart.clone().add(7, 'days')
        this.weekEnd   = this.weekStart.clone().add(6, 'days')
        this.loadWeek().then(() => this.openDay(this.weekDays[0]))
      }
    },

    // ===== Data loading =====
    async loadWeek() {
      if (!this.order_type) return Promise.resolve()
      await this.syncTruckAvailability(false)
      return Promise.all([
        this.loadInvoices(),
        this.loadCountsForWeek(),
        this.loadCapacity(),
        this.loadTruckAvailability(),
      ])
    },
    loadInvoices() {
      return new Promise(resolve => {
        Requestor.getAsyncP(
          '/api/zones/get_ready_to_schedule_invoices/',
          {
            order_type:  this.order_type,
            week_start:  this.weekStart.format('MM-DD-YYYY'),
            week_end:    this.weekEnd.format('MM-DD-YYYY'),
            location_id: this.locationId || undefined,
          },
          this,
          (r) => { this.invoicesByDate = r.data.by_date || {}; resolve() },
          (e) => { console.error('loadInvoices', e); resolve() }
        )
      })
    },
    loadCountsForWeek() {
      const days = this.weekDays
      const promises = days.map(day => new Promise(resolve => {
        Requestor.getAsyncP(
          '/api/zones/get_day_zone_counts/',
          { delivery_date: day.dateStr },
          this,
          (r) => {
            this.$set(this.countsByDate, day.dateStr, r.data.result || {})
            resolve()
          },
          () => { this.$set(this.countsByDate, day.dateStr, {}); resolve() }
        )
      }))
      return Promise.all(promises)
    },
    loadCapacity() {
      return new Promise(resolve => {
        Requestor.getAsyncP(
          '/api/zones/get_zone_day_capacity_week/',
          {},
          this,
          (r) => {
            const res = r.data.result || {}
            this.zones = res.zones || []
            this.capacityMatrix = res.matrix || {}
            resolve()
          },
          (e) => { console.error('loadCapacity', e); resolve() }
        )
      })
    },

    // ===== Truck availability =====
    syncTruckAvailability(showToast = false) {
      if (!this.weekStart || !this.weekEnd) return Promise.resolve()

      this.truckSync.loading = true
      return new Promise(resolve => {
        Requestor.postAsyncP(
          '/api/zones/sync_week_truck_availability/',
          {
            company_id:    90,
            week_start:    this.weekStart.format('MM-DD-YYYY'),
            week_end:      this.weekEnd.format('MM-DD-YYYY'),
            location_name: this.locationName || undefined,
          },
          this,
          (r) => {
            this.truckSync.loading = false
            this.truckSync.lastSync = moment().format('MMM D, HH:mm')

            // The sync response itself contains the truck data under result.days
            // — use it directly, no need to re-fetch.
            const syncResult = (r.data && r.data.result) || null
            if (syncResult && syncResult.days) {
              this.truckAvailability = syncResult.days
              console.log('[TRUCK] sync payload used directly:', this.truckAvailability)
              if (showToast) {
                this.$bvToast.toast(
                  `Synced ${syncResult.synced || 0} day(s) from Envio.`,
                  { variant: 'success', autoHideDelay: 2000 }
                )
              }
              resolve()
              return
            }

            // Fallback: still reload from GET (in case backend shape changes)
            console.warn('[TRUCK] sync returned no .days — falling back to GET')
            this.loadTruckAvailability().then(resolve)
            if (showToast) {
              this.$bvToast.toast('Sync completed but returned no data.',
                { variant: 'warning', autoHideDelay: 2500 })
            }
          },
          (e) => {
            this.truckSync.loading = false
            if (showToast) {
              const msg = (e.response && e.response.data && e.response.data.error)
                || 'Failed to sync truck availability from Envio.'
              this.$bvToast.toast(msg,
                { variant: 'danger', autoHideDelay: 3500, title: 'Sync failed' })
            }
            resolve()
          }
        )
      })
    },
  loadTruckAvailability() {
  return new Promise(resolve => {
    Requestor.getAsyncP(
      '/api/zones/get_week_truck_availability/',
      {
        week_start: this.weekStart.format('MM-DD-YYYY'),
        week_end:   this.weekEnd.format('MM-DD-YYYY'),
      },
      this,
      (r) => {
        const res = r.data.result || {}
        let incoming = {}
        if (res.days && typeof res.days === 'object') incoming = res.days
        else if (res && typeof res === 'object' && !Array.isArray(res)) incoming = res

        // Don't clobber good data with an empty response
        if (Object.keys(incoming).length > 0) {
          this.truckAvailability = incoming
        }
        this.weekTruckTotal = r.data.total || 0
        resolve()
      },
      () => resolve()   // keep whatever we already had on error
    )
  })
},

    // ===== Cell events =====
    onToggleCell({ zone_id, dow, is_active }) {
      const key = `${zone_id}-${dow}`
      const cur = (this.capacityMatrix[zone_id] || {})[dow] || { max_orders: 0, priority: 0 }

      if (is_active && cur.max_orders > 0) {
        const dayTotal = this.dailyCapacityByDow[dow] || 0
        if (dayTotal > 0) {
          let otherTotal = 0
          Object.keys(this.capacityMatrix).forEach(zid => {
            if (String(zid) === String(zone_id)) return
            const cell = (this.capacityMatrix[zid] || {})[dow]
            if (cell && cell.is_active) otherTotal += (cell.max_orders || 0)
          })
          if (otherTotal + cur.max_orders > dayTotal) {
            const remaining = Math.max(0, dayTotal - otherTotal)
            this.$bvToast.toast(
              `Cannot activate: this would exceed day capacity (${dayTotal}). ` +
              `Only ${remaining} slot(s) left for this day.`,
              { variant: 'warning', autoHideDelay: 4000, title: 'Capacity Exceeded' }
            )
            return
          }
        }
      }

      this.$set(this.savingCells, key, true)
      Requestor.postAsyncP(
        '/api/zones/set_zone_day_capacity/',
        {
          zone_id, day_of_week: dow, is_active,
          max_orders: cur.max_orders || 0,
          priority:   cur.priority   || 0,
          delivery_date: this.dowToDateStr(dow),
        },
        this,
        (r) => {
          const saved = r.data.result || {}
          if (!this.capacityMatrix[zone_id]) this.$set(this.capacityMatrix, zone_id, {})
          this.$set(this.capacityMatrix[zone_id], dow, {
            is_active:  saved.is_active,
            max_orders: saved.max_orders,
            priority:   saved.priority,
          })
          this.$delete(this.savingCells, key)
        },
        (e) => {
          this.$delete(this.savingCells, key)
          const msg = (e.response && e.response.data && e.response.data.error)
            || 'Failed to save toggle'
          this.$bvToast.toast(msg,
            { variant: 'danger', autoHideDelay: 4000, title: 'Save failed' })
        }
      )
    },

    onMaxChange({ zone_id, dow, max_orders }) {
      const requested = parseInt(max_orders, 10) || 0

      const dayTotal = this.dailyCapacityByDow[dow] || 0
      if (dayTotal > 0) {
        let otherTotal = 0
        Object.keys(this.capacityMatrix).forEach(zid => {
          if (String(zid) === String(zone_id)) return
          const cell = (this.capacityMatrix[zid] || {})[dow]
          if (cell && cell.is_active) otherTotal += (cell.max_orders || 0)
        })

        if (otherTotal + requested > dayTotal) {
          const remaining = Math.max(0, dayTotal - otherTotal)
          const rec = this.truckAvailability[this.dowToDateStr(dow)] || {}
          this.$bvToast.toast(
            `Day capacity is ${dayTotal} ` +
            `(${rec.trucks_available || 0} trucks × ${rec.max_orders_per_truck || 0} orders). ` +
            `Other zones already use ${otherTotal}. ` +
            `Max you can assign to this zone: ${remaining}.`,
            { variant: 'warning', autoHideDelay: 5000, title: 'Capacity Exceeded' }
          )
          return
        }
      }

      const key = `${zone_id}-${dow}`
      this.$set(this.savingCells, key, true)
      const cur = (this.capacityMatrix[zone_id] || {})[dow] || { is_active: true, priority: 0 }
      Requestor.postAsyncP(
        '/api/zones/set_zone_day_capacity/',
        {
          zone_id, day_of_week: dow,
          is_active: cur.is_active,
          max_orders: requested,
          priority: cur.priority || 0,
          delivery_date: this.dowToDateStr(dow),
        },
        this,
        (r) => {
          const saved = r.data.result || {}
          if (!this.capacityMatrix[zone_id]) this.$set(this.capacityMatrix, zone_id, {})
          this.$set(this.capacityMatrix[zone_id], dow, {
            is_active:  saved.is_active,
            max_orders: saved.max_orders,
            priority:   saved.priority,
          })
          this.$delete(this.savingCells, key)
        },
        (e) => {
          this.$delete(this.savingCells, key)
          const msg = (e.response && e.response.data && e.response.data.error)
            || 'Failed to save max orders'
          this.$bvToast.toast(msg,
            { variant: 'danger', autoHideDelay: 5000, title: 'Save failed' })
        }
      )
    },

    dowToDateStr(dow) {
      if (!this.weekStart) return ''
      return this.weekStart.clone().add(dow - 1, 'days').format('MM-DD-YYYY')
    },

    distributeDay(day) {
      if (!this.isValidDay(day)) return
      Requestor.postAsyncP(
        '/api/zones/distribute_orders_to_trucks/',
        { delivery_date: day.dateStr, order_type: this.order_type },
        this,
        (r) => {
          const res = r.data.result || {}
          this.$bvToast.toast(
            `Assigned ${res.orders_assigned || 0} · Moved ${res.orders_moved || 0} · Leftover ${res.orders_unassigned || 0}`,
            { variant: 'success', autoHideDelay: 3500 }
          )
          this.loadWeek()
        },
        (e) => {
          const err = (e.response && e.response.data && e.response.data.error) || 'Distribution failed'
          this.$bvToast.toast(err, { variant: 'danger', autoHideDelay: 3000 })
        }
      )
    },

    openReschedModal(inv) {
      if (!inv) return
      this.reschedModal = {
        invoice: inv,
        delivery_date: inv.delivery_date
          ? moment(inv.delivery_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
          : '',
      }
      this.$bvModal.show('rts-resched-modal')
    },
    saveResched() {
      const m = this.reschedModal
      if (!m.invoice || !m.delivery_date) return
      const d = moment(m.delivery_date, 'YYYY-MM-DD').format('MM-DD-YYYY')
      const updates = [{
        invoice_id: m.invoice.invoice_id,
        delivery_date: d,
        pull_date: moment(d, 'MM-DD-YYYY').subtract(1, 'day').format('MM-DD-YYYY'),
      }]
      Requestor.postAsyncP(
        '/api/sales/reschedule_delivery_dates/',
        { updates },
        this,
        () => {
          this.$bvModal.hide('rts-resched-modal')
          this.loadWeek()
          this.$bvToast.toast('Rescheduled', { variant: 'success', autoHideDelay: 1500 })
        },
        () => this.$bvToast.toast('Failed to reschedule', { variant: 'danger' })
      )
    },
  },
}
</script>

<style scoped>
.rts-wrapper { width: 100%; font-size: 16px; color: #1a202c; }

/* Picker */
.rts-picker { padding: 80px 20px; text-align: center; }
.rts-picker-title { font-size: 48px; font-weight: 900; margin-bottom: 12px; }
.rts-picker-sub { font-size: 20px; color: #718096; margin-bottom: 48px; }
.rts-picker-cards { display: flex; gap: 32px; justify-content: center; flex-wrap: wrap; }
.rts-pick-card {
  width: 320px; padding: 48px 32px; background: white;
  border: 3px solid #e2e8f0; border-radius: 24px; cursor: pointer;
  transition: all 0.2s; box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}
.rts-pick-card:hover { transform: translateY(-6px); border-color: #4299e1;
  box-shadow: 0 24px 48px rgba(66,153,225,0.25); }
.rts-pick-card i { font-size: 64px; color: #4299e1; margin-bottom: 20px; }
.rts-pick-label { font-size: 24px; font-weight: 800; margin-bottom: 16px; }
.rts-pick-count { font-size: 56px; font-weight: 900; line-height: 1; }
.rts-pick-sub { font-size: 14px; font-weight: 700; color: #a0aec0;
  text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; }

/* Toolbar */
.rts-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px; padding: 16px 24px; background: white;
  border-radius: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  flex-wrap: wrap; gap: 14px;
}
.rts-toolbar-left, .rts-toolbar-right { display: flex; align-items: center; gap: 14px; }
.rts-btn-back {
  background: #edf2f7; border: none; border-radius: 10px; padding: 12px 20px;
  cursor: pointer; font-weight: 800; font-size: 15px; color: #2d3748;
  display: inline-flex; align-items: center; gap: 8px;
}
.rts-btn-back:hover { background: #e2e8f0; }
.rts-badge { padding: 12px 22px; border-radius: 24px; color: white;
  font-weight: 900; font-size: 15px; letter-spacing: 0.5px;
  display: inline-flex; align-items: center; gap: 8px; }
.rts-badge-hd { background: #4299e1; }
.rts-badge-sp { background: #9c27b0; }

.rts-btn-ghost {
  background: white; border: 2px solid #4299e1; color: #4299e1;
  border-radius: 10px; padding: 10px 18px; cursor: pointer;
  font-weight: 800; font-size: 14px;
  display: inline-flex; align-items: center; gap: 8px;
  transition: all .18s;
}
.rts-btn-ghost:hover:not(:disabled) { background: #4299e1; color: white; }
.rts-btn-ghost:disabled { opacity: .6; cursor: not-allowed; }
.rts-sync-meta { font-size: 12px; color: #a0aec0; font-weight: 600; }

/* Modal */
.rts-modal-body { padding: 12px; font-size: 16px; }
.rts-modal-sub { color: #4a5568; margin-bottom: 20px; font-size: 17px; }
.rts-field { margin-bottom: 18px; }
.rts-field label {
  display: block; font-size: 14px; font-weight: 900; color: #4a5568;
  margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;
}
.rts-field-input {
  width: 100%; padding: 12px 14px; border: 2px solid #cbd5e0;
  border-radius: 10px; font-size: 16px; font-weight: 600;
}
.rts-field-input:focus { outline: none; border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66,153,225,0.2); }
.rts-modal-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; }
.rts-btn-cancel { background: #edf2f7; border: none; border-radius: 10px;
  padding: 12px 24px; font-weight: 800; cursor: pointer; font-size: 15px;
  text-transform: uppercase; }
.rts-btn-save { background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white; border: none; border-radius: 10px; padding: 12px 24px;
  font-weight: 900; cursor: pointer; font-size: 15px; text-transform: uppercase; }

@media (max-width: 768px) {
  .rts-picker-title { font-size: 32px; }
  .rts-pick-count { font-size: 42px; }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/ReadyToScheduleCalendar.vue